import { useTranslation } from 'react-i18next';
import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useDocumentParams } from 'app/router/params';
import { Typography } from 'app/components';
import { i18n, type MessageKey } from 'shared/i18n';
import { TagItemShortcuts, TagAction } from '../../TagItemShortcuts/TagItemShortcuts';
import {
  useUpdateDocumentTaxonomyConfig,
  useTagAttributeCounts,
  documentTaxonomyConfigQueryKeys,
  DocumentTaxonomyConfig,
  TagCountAttribute,
  TagAttributeCounts,
} from 'entities/tag';
import { PropertyDefault } from '../types/taxonomy-settings';
import { AddPropertyModal, AffectedTagsModal } from './modals';
import { useBatchedConfigUpdate } from '../hooks';
import { useQueryClient } from '@tanstack/react-query';
import Tippy from '@tippyjs/react';

import { MissingDefaultHint } from './MissingDefaultHint';

import './Section.scss';

const PROPERTY_ORDER: PropertyDefault['type'][] = ['scale', 'format', 'decimals', 'precision'];

const PROPERTY_LABELS: Record<PropertyDefault['type'], MessageKey> = {
  scale: 'tag:scale.title',
  format: 'tag:format.title',
  decimals: 'tag:accuracy.decimals',
  precision: 'tag:accuracy.title',
};

const TAG_COUNT_ATTRIBUTES: TagCountAttribute[] = ['scale', 'decimals', 'precision'];

const sumTagCounts = (counts: TagAttributeCounts | undefined, attribute: TagCountAttribute): number => {
  const entries = counts?.[attribute];
  if (!entries || entries.length === 0) return 0;
  return entries.reduce((sum, entry) => sum + entry.count, 0);
};

interface PropertyDefaultsSectionProps {
  configId: string;
  config: DocumentTaxonomyConfig;
  scaleMissing: boolean;
  accuracyMissing: boolean;
}

interface DeleteTarget {
  type: PropertyDefault['type'];
  label: string;
  count: number;
}

export const PropertyDefaultsSection = ({ configId, config, scaleMissing, accuracyMissing }: PropertyDefaultsSectionProps) => {
  const { t } = useTranslation(['common', 'tag']);

  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [editPropertyData, setEditPropertyData] = useState<PropertyDefault | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();

  const queryClient = useQueryClient();
  const { mutate: updateConfig } = useUpdateDocumentTaxonomyConfig();
  const { update: batchedUpdate, flush } = useBatchedConfigUpdate({ configId, updateConfig });

  const { data: tagAttributeCounts } = useTagAttributeCounts(configId, TAG_COUNT_ATTRIBUTES);

  const { items, availableTypes, hasBothAccuracyTypes } = useMemo(() => {
    const configValues: Record<PropertyDefault['type'], number | string | null | undefined> = {
      scale: config.scale,
      format: config.format,
      decimals: config.decimals,
      precision: config.precision,
    };

    const hasBothAccuracyTypes = config.decimals != null && config.precision != null;
    const items: PropertyDefault[] = [];
    const availableTypes: PropertyDefault['type'][] = [];

    for (const type of PROPERTY_ORDER) {
      const value = configValues[type];
      if (value != null) {
        const isActive = hasBothAccuracyTypes && (type === 'decimals' || type === 'precision') ? config.preferredFormat === type : undefined;
        items.push({ id: `config-${type}`, type, value, referencedTags: type === 'format' ? 0 : sumTagCounts(tagAttributeCounts, type), isActive });
      } else {
        availableTypes.push(type);
      }
    }

    return { items, availableTypes, hasBothAccuracyTypes };
  }, [config.scale, config.decimals, config.precision, config.format, config.preferredFormat, tagAttributeCounts]);

  const updatePropertyInConfig = useCallback(
    (data: { type: PropertyDefault['type']; value: number | string | null }) => {
      const coerced =
        data.type === 'format'
          ? typeof data.value === 'string'
            ? data.value
            : null
          : (data.type === 'decimals' || data.type === 'precision') && data.value === 'INF'
          ? 'INF'
          : typeof data.value === 'number'
          ? data.value
          : null;
      const updates: Record<string, number | string | null> = { [data.type]: coerced };

      if ((data.type === 'decimals' || data.type === 'precision') && coerced != null && !config.preferredFormat) {
        updates.preferredFormat = data.type;
        queryClient.setQueryData<DocumentTaxonomyConfig | null>([documentTaxonomyConfigQueryKeys.configByDocument, config.documentSuperId], (old) =>
          old ? { ...old, preferredFormat: data.type } : null
        );
      }

      batchedUpdate(updates);
      setEditPropertyData(null);
    },
    [batchedUpdate, config.preferredFormat, config.documentSuperId, queryClient]
  );

  const handleSetPreferred = (type: 'decimals' | 'precision') => {
    queryClient.setQueryData<DocumentTaxonomyConfig | null>([documentTaxonomyConfigQueryKeys.configByDocument, config.documentSuperId], (old) =>
      old ? { ...old, preferredFormat: type } : null
    );
    batchedUpdate({ preferredFormat: type });
    flush();
  };

  const handleDeleteClick = (item: PropertyDefault) => {
    if (item.referencedTags > 0) {
      setDeleteTarget({ type: item.type, label: i18n(PROPERTY_LABELS[item.type]), count: item.referencedTags });
    } else {
      updatePropertyInConfig({ type: item.type, value: null });
    }
  };

  const handleGoToTags = () => {
    if (!deleteTarget) return;
    navigate({
      to: '/documents/$documentId/tagging',
      params: { documentId },
      search: {
        tagFilter: { type: deleteTarget.type, value: deleteTarget.type, label: deleteTarget.label },
      },
    });
    setDeleteTarget(null);
  };

  const handleOpenEdit = (property: PropertyDefault) => {
    setEditPropertyData(property);
    setShowPropertyModal(true);
  };

  const handleOpenAdd = () => {
    setEditPropertyData(null);
    setShowPropertyModal(true);
  };

  const handleCloseModal = () => {
    setShowPropertyModal(false);
    setEditPropertyData(null);
  };

  return (
    <section className="defaults-section">
      <div className="defaults-section__header">
        <Typography variant="body-8" fontWeight="semibold">
          {`${t('tag:property.label')} ${t('tag:default.label')}`}
        </Typography>

        <button className="custom-btn" onClick={handleOpenAdd} disabled={availableTypes.length === 0}>
          <i className="fa-regular fa-plus" />
          {availableTypes.length === 0 ? t('tag:nothing.to.add') : `${t('common:add')} ${t('tag:property.label')}`}
        </button>
      </div>

      {(scaleMissing || accuracyMissing) && (
        <div className="defaults-section__hints">
          {scaleMissing && <MissingDefaultHint labelKey="defaults.missing.scale" />}
          {accuracyMissing && <MissingDefaultHint labelKey="defaults.missing.accuracy" />}
        </div>
      )}

      {items.length === 0 ? (
        <div className="empty-state">
          <Typography variant="body-7" color="#6c757d">
            {t('tag:property.empty')}
          </Typography>
        </div>
      ) : (
        <div className="defaults-list">
          {items.map((item) => {
            const isPreferrable = hasBothAccuracyTypes && (item.type === 'decimals' || item.type === 'precision');
            const isPreferred = isPreferrable && item.isActive;

            const actions: TagAction[] = [
              { type: 'edit', onClick: () => handleOpenEdit(item) },
              { type: 'delete', onClick: () => handleDeleteClick(item) },
            ];

            if (isPreferrable && !item.isActive) {
              actions.unshift({ type: 'default', onClick: () => handleSetPreferred(item.type as 'decimals' | 'precision') });
            }

            return (
              <div key={item.id} className={`default-item ${isPreferred ? 'default-item--primary' : ''}`}>
                <div className="default-item__main">
                  <div className="default-item__indicator">
                    {isPreferred ? (
                      <Tippy
                        content={
                          <Typography variant="body-5" color="#6c757d" className="defaults-section__tippy-content">
                            {t('tag:preferred.format')}
                          </Typography>
                        }
                      >
                        <i className="fa-solid fa-star fa-xs" />
                      </Tippy>
                    ) : !isPreferrable ? (
                      <i className="fa-solid fa-circle-small" />
                    ) : null}
                  </div>

                  <div className="default-item__content">
                    <Typography variant="body-7" fontWeight="semibold">
                      {i18n(PROPERTY_LABELS[item.type])}: {item.value}
                    </Typography>
                  </div>
                </div>

                <div className="default-item__meta">
                  <Typography variant="body-5" color="#547BA2">
                    {item.referencedTags} {t('tag:active.tags')}
                  </Typography>
                  <TagItemShortcuts actions={actions} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddPropertyModal
        show={showPropertyModal}
        availableTypes={availableTypes}
        editData={editPropertyData ? { id: editPropertyData.id, type: editPropertyData.type, value: editPropertyData.value } : null}
        onClose={handleCloseModal}
        onAdd={updatePropertyInConfig}
        onEdit={updatePropertyInConfig}
      />

      <AffectedTagsModal
        show={!!deleteTarget}
        affectedCount={deleteTarget?.count ?? 0}
        itemLabel={deleteTarget?.label ?? ''}
        onClose={() => setDeleteTarget(null)}
        onGoToTags={handleGoToTags}
      />
    </section>
  );
};
